import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface SchoolCalendarEntry {
  id: string
  studentId: string
  studentName: string | null
  instructorId: string
  instructorName: string | null
  packageName: string | null
  scheduledAt: string
  plannedEndAt: string | null
  status: 'scheduled' | 'completed' | 'cancelled'
}

/**
 * Read-only view of every instructor's upcoming (status='scheduled') lessons at the
 * school currently selected, for the "all instructors" calendar grid. Same trust
 * boundary as useSchoolInstructorCalendar — relies entirely on the pre-existing
 * "driving_lessons: school admin read" RLS policy (migration 092), scoped by
 * driving_lessons.school_id; this hook just drops that hook's single-instructor filter.
 * Deliberately scoped to 'scheduled' only (not completed/cancelled) — this is a
 * forward-looking planning grid, not a lesson history, matching how "Upcoming lessons"
 * works everywhere else in the app.
 */
export function useSchoolCalendarAllInstructors(schoolId: string | null) {
  const [lessons, setLessons] = useState<SchoolCalendarEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchLessons = useCallback(async () => {
    if (!schoolId) { setLessons([]); setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      const { data: lessonRows, error: lessonErr } = await supabase
        .from('driving_lessons')
        .select('id, student_id, instructor_id, scheduled_at, planned_end_at, status')
        .eq('school_id', schoolId)
        .eq('status', 'scheduled')
        .order('scheduled_at', { ascending: true })

      if (lessonErr) { setError('Could not load the school calendar.'); return }
      if (!lessonRows || lessonRows.length === 0) { setLessons([]); return }

      const studentIds = [...new Set(lessonRows.map(l => l.student_id))]
      const instructorIds = [...new Set(lessonRows.map(l => l.instructor_id))]
      const allProfileIds = [...new Set([...studentIds, ...instructorIds])]

      const [profilesRes, relationshipsRes] = await Promise.all([
        supabase.from('profiles').select('id, full_name').in('id', allProfileIds),
        supabase
          .from('instructor_student_relationships')
          .select('student_id, package_id, school_packages(name)')
          .eq('school_id', schoolId)
          .eq('status', 'active'),
      ])

      const nameById = new Map((profilesRes.data ?? []).map(p => [p.id, p.full_name]))
      const packageByStudent = new Map(
        (relationshipsRes.data ?? []).map(r => [r.student_id, (r as any).school_packages?.name ?? null])
      )

      setLessons(lessonRows.map(l => ({
        id: l.id,
        studentId: l.student_id,
        studentName: nameById.get(l.student_id) ?? null,
        instructorId: l.instructor_id,
        instructorName: nameById.get(l.instructor_id) ?? null,
        packageName: packageByStudent.get(l.student_id) ?? null,
        scheduledAt: l.scheduled_at,
        plannedEndAt: l.planned_end_at,
        status: l.status,
      })))
    } finally {
      setLoading(false)
    }
  }, [schoolId])

  useEffect(() => { fetchLessons() }, [fetchLessons])

  return { lessons, loading, error, refresh: fetchLessons }
}
